<script setup>
import { Head } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import { isBrandDirect as checkBrandDirect } from '@/lib/brand';
import LeadFunnel from '@/Components/LeadFunnel.vue';
import { useCopy } from '@/composables/useCopy';

const props = defineProps({
    domain: Object,
    service: String,
    branding: Object,
    designVariant: Object,
    copyPack: { type: Object, default: () => ({}) },
    spotImages: { type: Object, default: () => ({}) },
});

const { t, ts, spot } = useCopy(props);

const serviceLabel = props.service === 'wp' ? 'Wärmepumpen' : 'Photovoltaik';
const isBrandDirect = checkBrandDirect(props.domain);
const brandName = props.domain?.settings?.brand_name || props.domain?.name || 'Portal';
</script>

<template>
    <Head :title="isBrandDirect ? `Über ${brandName}` : 'Über uns - Dein Vergleichsportal'">
        <meta name="description" :content="`${brandName}: Vergleichsportal für ${serviceLabel} - für Studierende und Hauseigentümer, kostenlos und unverbindlich.`" />
        <meta property="og:type" content="website" />
    </Head>

    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" activePage="about" heroMode>
        <!-- Hero -->
        <section class="sv-hero" aria-label="Über uns">
            <div class="sv-hero__bg" aria-hidden="true"></div>
            <div class="sv-hero__content">
                <span class="sv-hero__tag">{{ t('about_hero_tag', 'Über uns') }}</span>
                <h1 v-if="isBrandDirect" class="sv-hero__title">{{ t('about_hero_title_brand', `Über ${brandName}.`) }}</h1>
                <h1 v-else class="sv-hero__title">{{ t('about_hero_title_pre', 'Wir machen Deutschland') }} <span class="sv-hero__accent">{{ t('about_hero_title_accent', 'grüner.') }}</span></h1>
                <p class="sv-hero__sub">{{ t('about_hero_sub', 'Als Vergleichsportal verbinden wir dich mit regionalen Fachbetrieben - kostenlos und unverbindlich. Beratung durch Studierende, Umsetzung durch Fachbetriebe.') }}</p>
            </div>
        </section>

        <!-- Mission -->
        <section class="sv-mission" aria-label="Unsere Mission">
            <div class="sv-mission__inner">
                <div
                    class="sv-mission__image"
                    role="img"
                    :aria-label="t('about_mission_image_alt', 'Mission')"
                    :style="spot('about_mission') ? { backgroundImage: `url(${spot('about_mission')})` } : {}"
                ></div>
                <div class="sv-mission__text">
                    <span class="sv-mission__eyebrow">{{ t('about_mission_eyebrow', 'Unsere Mission') }}</span>
                    <h2 v-if="isBrandDirect" class="sv-mission__heading">{{ t('about_mission_heading_brand', 'Unser Anspruch.') }}</h2>
                    <h2 v-else class="sv-mission__heading">{{ t('about_mission_heading', 'Warum wir tun, was wir tun.') }}</h2>
                    <p class="sv-mission__body">{{ ts('about_mission_body_1', 'StudiVolt ist für Studierende und Hauseigentümer: Wir erklären den Markt in klarer Sprache und leiten deine Anfrage an regionale Fachbetriebe weiter.') }}</p>
                    <p class="sv-mission__body">{{ t('about_mission_body_2', 'Der Vertrag kommt direkt zwischen dir und dem Betrieb zustande. Für dich ist der Vergleich kostenlos. Wir finanzieren uns über eine Vermittlungsvergütung.') }}</p>
                    <div class="sv-mission__trust">
                        <span class="sv-mission__trust-item">{{ isBrandDirect ? t('about_mission_trust_1_brand', 'Persönlicher Service') : t('about_mission_trust_1', 'Kostenlos für Sie') }}</span>
                        <span class="sv-mission__trust-item">{{ t('about_mission_trust_2', 'Seit 2024 aktiv') }}</span>
                        <span class="sv-mission__trust-item">{{ t('about_mission_trust_3', 'Bundesweit') }}</span>
                    </div>
                </div>
            </div>
        </section>

        <!-- Stats -->
        <section class="sv-stats" aria-label="Zahlen">
            <div class="sv-stats__inner">
                <h2 class="sv-stats__heading">{{ t('about_stats_heading', 'Unsere Zahlen sprechen für sich') }}</h2>
                <div class="sv-stats__grid">
                    <div v-for="stat in [
                        { value: t('about_stats_1_value', 'Für dich'), label: t('about_stats_1_label', 'kostenlos & unverbindlich') },
                        { value: t('about_stats_2_value', 'DE'), label: t('about_stats_2_label', 'Regionale Fachbetriebe') },
                        { value: t('about_stats_3_value', 'Offen'), label: t('about_stats_3_label', 'Vermittlungsvergütung') },
                        { value: t('about_stats_4_value', 'Bis zu 3'), label: t('about_stats_4_label', 'Angebote zum Vergleich') }
                    ]" :key="stat.label" class="sv-stat-card">
                        <div class="sv-stat-card__value">{{ stat.value }}</div>
                        <div class="sv-stat-card__label">{{ stat.label }}</div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Values -->
        <section class="sv-values" aria-label="Unsere Werte">
            <div class="sv-values__inner">
                <h2 class="sv-values__heading">{{ t('about_values_heading', 'Was uns auszeichnet') }}</h2>
                <div class="sv-values__grid">
                    <div v-for="(item, idx) in [
                        { title: t('about_values_1_title', 'Vergleichsportal'), desc: t('about_values_1_desc', 'Wir vermitteln Anfragen an regionale Fachbetriebe. Ob und mit wem du abschließt, entscheidest du.') },
                        { title: t('about_values_2_title', 'Transparent'), desc: t('about_values_2_desc', 'Keine versteckten Kosten. Du erhältst echte Vergleichsangebote von qualifizierten Fachbetrieben.') },
                        { title: t('about_values_3_title', 'Regional'), desc: t('about_values_3_desc', 'Wir kennen die lokalen Fördermittel und verbinden dich mit Betrieben vor Ort.') },
                    ]" :key="item.title" class="sv-value-card">
                        <div class="sv-value-card__num">{{ idx + 1 }}</div>
                        <h3 class="sv-value-card__title">{{ item.title }}</h3>
                        <p class="sv-value-card__desc">{{ item.desc }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA -->
        <section class="sv-cta-dark" aria-label="Kontakt">
            <div class="sv-cta-dark__inner">
                <h2 class="sv-cta-dark__heading">{{ t('about_cta_heading', 'Hast du Fragen?') }}</h2>
                <p class="sv-cta-dark__sub">{{ t('about_cta_sub', 'Kontaktiere uns - wir beraten dich gerne kostenlos und unverbindlich.') }}</p>
                <div class="sv-cta-dark__funnel">
                    <div class="sv-funnel-wrap">
                        <LeadFunnel :domain="domain" :service="service" :design-variant="designVariant" bare :dark-mode="false" />
                    </div>
                </div>
            </div>
        </section>
    </AppLayout>
</template>

<style scoped>
.sv-hero { position: relative; min-height: 420px; display: flex; align-items: center; overflow: hidden; background: #0a1628; }
.sv-hero__bg { position: absolute; inset: 0; background: linear-gradient(135deg, #0a1628 0%, #001d75 100%); opacity: 0.95; }
.sv-hero__content { position: relative; z-index: 2; max-width: 720px; padding: 100px 24px 60px; }
@media (min-width: 768px) { .sv-hero__content { padding: 120px 48px 70px; margin-left: max(calc((100% - 1200px) / 2), 48px); } }
.sv-hero__tag { display: inline-block; padding: 6px 18px; border-radius: 20px; font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #00d782; border: 1px solid rgba(0,215,130,0.3); background: rgba(0,215,130,0.08); margin-bottom: 20px; }
.sv-hero__title { font-family: 'Inter', sans-serif; font-size: clamp(32px, 5vw, 52px); font-weight: 800; color: #fff; line-height: 1.1; margin-bottom: 16px; }
.sv-hero__accent { color: #00d782; }
.sv-hero__sub { font-family: 'Inter', sans-serif; font-size: 17px; line-height: 1.7; color: rgba(255,255,255,0.7); max-width: 540px; }

.sv-mission { padding: 80px 0; background: #ffffff; }
@media (min-width: 768px) { .sv-mission { padding: 100px 0; } }
.sv-mission__inner { max-width: 1100px; margin: 0 auto; padding: 0 24px; display: flex; flex-direction: column; gap: 32px; }
@media (min-width: 1024px) { .sv-mission__inner { flex-direction: row; gap: 48px; } }
.sv-mission__image { width: 100%; min-height: 260px; border-radius: 16px; background: linear-gradient(135deg, #001d75, #0a1628); background-size: cover; background-position: center; }
@media (min-width: 1024px) { .sv-mission__image { width: 50%; } }
.sv-mission__text { flex: 1; display: flex; flex-direction: column; justify-content: center; }
.sv-mission__eyebrow { font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 1.4px; text-transform: uppercase; color: #00d782; margin-bottom: 10px; }
.sv-mission__heading { font-family: 'Inter', sans-serif; font-size: clamp(24px, 3vw, 32px); font-weight: 800; color: #1a1a1a; margin-bottom: 16px; padding-bottom: 16px; border-bottom: 2px solid rgba(0,215,130,0.2); }
.sv-mission__body { font-family: 'Inter', sans-serif; font-size: 15px; color: #64748b; line-height: 1.7; margin-bottom: 12px; }
.sv-mission__trust { display: flex; flex-wrap: wrap; gap: 12px 20px; margin-top: 20px; padding-top: 16px; border-top: 1px solid #e2e8f0; }
.sv-mission__trust-item { font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600; color: #1a1a1a; padding: 4px 12px; background: rgba(0,215,130,0.08); border-radius: 20px; }

.sv-stats { padding: 80px 0; background: #f0f4f8; }
.sv-stats__inner { max-width: 1000px; margin: 0 auto; padding: 0 24px; }
.sv-stats__heading { font-family: 'Inter', sans-serif; font-size: clamp(24px, 3vw, 32px); font-weight: 800; color: #1a1a1a; text-align: center; margin-bottom: 40px; }
.sv-stats__grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
@media (min-width: 1024px) { .sv-stats__grid { grid-template-columns: repeat(4, 1fr); } }
.sv-stat-card { text-align: center; padding: 32px 20px; background: #0f1d32; border-radius: 16px; }
.sv-stat-card__value { font-family: 'Inter', sans-serif; font-size: 40px; font-weight: 800; color: #00d782; line-height: 1; margin-bottom: 8px; }
.sv-stat-card__label { font-family: 'Inter', sans-serif; font-size: 14px; color: rgba(255,255,255,0.6); font-weight: 500; }

.sv-values { padding: 80px 0; background: #ffffff; }
.sv-values__inner { max-width: 1000px; margin: 0 auto; padding: 0 24px; }
.sv-values__heading { font-family: 'Inter', sans-serif; font-size: clamp(24px, 3vw, 32px); font-weight: 800; color: #1a1a1a; text-align: center; margin-bottom: 40px; }
.sv-values__grid { display: grid; grid-template-columns: 1fr; gap: 24px; }
@media (min-width: 768px) { .sv-values__grid { grid-template-columns: repeat(3, 1fr); } }
.sv-value-card { padding: 32px 24px; background: #0f1d32; border-radius: 16px; text-align: left; border: 1px solid rgba(0,215,130,0.18); }
.sv-value-card__num { width: 48px; height: 48px; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; border-radius: 50%; background: #00d782; color: #1a1a1a; font-family: 'Inter', sans-serif; font-size: 18px; font-weight: 800; }
.sv-value-card__title { font-family: 'Inter', sans-serif; font-size: 18px; font-weight: 700; color: #ffffff; margin-bottom: 10px; }
.sv-value-card__desc { font-family: 'Inter', sans-serif; font-size: 14px; color: rgba(255,255,255,0.6); line-height: 1.65; }

.sv-cta-dark { padding: 80px 0; background: #0a1628; }
@media (min-width: 768px) { .sv-cta-dark { padding: 100px 0; } }
.sv-cta-dark__inner { max-width: 700px; margin: 0 auto; padding: 0 24px; text-align: center; }
.sv-cta-dark__heading { font-family: 'Inter', sans-serif; font-size: clamp(24px, 3vw, 36px); font-weight: 800; color: #ffffff; margin-bottom: 12px; }
.sv-cta-dark__sub { font-family: 'Inter', sans-serif; font-size: 16px; color: rgba(255,255,255,0.65); line-height: 1.7; margin-bottom: 32px; }
.sv-cta-dark__funnel { max-width: 520px; margin: 0 auto; }
.sv-funnel-wrap { padding: 24px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; box-shadow: 0 8px 32px rgba(0,0,0,0.08); }
</style>
